import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { AuthContext } from "../../../../helpers/AuthContext";
import {
  exportArAgingCsv,
  fetchArAgingReport,
  fetchArAgingReportReps,
  searchReportCustomers,
} from "../../../../services/reports";
import ReportRepAsyncSelect from "../shared/ReportRepAsyncSelect";
import ReportColumnVisibilityButton from "../shared/ReportColumnVisibilityButton";
import ReportSavedReportsButton from "../shared/ReportSavedReportsButton";
import ReportSummaryCard from "../shared/ReportSummaryCard";
import { normalizeHiddenColumns as normalizeSavedHiddenColumns, parseSavedBoolean } from "../shared/savedReportUtils";
import { UITableShell, UITableToolbar } from "../../../../components/ui";
import {
  reportContentSx,
  reportFilterCardSx,
  reportFilterGridSx,
  reportPageWrapperSx,
  reportShellSx,
  reportTableBodySx,
  reportTableContainerSx,
  reportTableHeaderCellSx,
  reportTablePaperSx,
  reportTableSectionSx,
} from "../shared/reportLayout";
import { useParams } from "react-router-dom";

const DEFAULT_FILTERS = {
  asOfDate: new Date().toISOString().slice(0, 10),
  client: "",
  clientId: "",
  repId: "",
  assignmentFilter: "anyAssignment",
  bucket: "all",
  quickFind: "",
  includeCredits: "false",
};

const DEFAULT_SUMMARY = {
  totalAr: 0,
  current: 0,
  oneToThirty: 0,
  thirtyOneToSixty: 0,
  sixtyOneToNinety: 0,
  ninetyOnePlus: 0,
  pastDueTotal: 0,
  customerCount: 0,
};

const DEFAULT_OPTIONS = {
  buckets: [
    { value: "all", label: "All" },
    { value: "current", label: "Current" },
    { value: "1-30", label: "1 - 30" },
    { value: "31-60", label: "31 - 60" },
    { value: "61-90", label: "61 - 90" },
    { value: "91+", label: "91 and Over" },
  ],
  assignmentFilters: [
    { value: "anyAssignment", label: "Any Assignment" },
    { value: "clientRep", label: "Client Rep" },
    { value: "orderRep", label: "Order Rep" },
    { value: "administratorFallback", label: "Administrator / Fallback" },
  ],
};

const roleNormalized = (value = "") => String(value || "").replace(/_/g, "-").trim().toLowerCase();
const currency = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const friendlyDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};
const customerOptionLabel = (option) => option?.label || option?.businessName || option?.name || "";
const normalizeAvatarUrl = (imgSrc) => {
  if (!imgSrc) return null;
  if (Array.isArray(imgSrc)) {
    const selectedPic = imgSrc.find((pic) => pic?.selected) || imgSrc[0];
    return normalizeAvatarUrl(selectedPic?.url || null);
  }
  if (typeof imgSrc === "string") {
    if (/^https?:\/\//i.test(imgSrc)) return imgSrc;
    if (imgSrc.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${imgSrc}`;
    if (imgSrc.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${imgSrc}`;
    try {
      const parsed = JSON.parse(imgSrc);
      if (Array.isArray(parsed)) return normalizeAvatarUrl(parsed);
    } catch {
      return imgSrc;
    }
  }
  return imgSrc;
};
const repDisplayName = (rep) => {
  const name = [rep?.first_name || rep?.firstName, rep?.last_name || rep?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || rep?.email || (rep?.id != null ? `User ${rep.id}` : "");
};
const repInitials = (name) =>
  String(name || "")
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
const normalizeSavedCustomerOption = (customer) => {
  if (!customer) return null;
  const id = customer?.id != null && String(customer.id).trim() !== "" ? String(customer.id) : "";
  const label = customer?.label || customer?.businessName || customer?.name || customer?.emailAddress || (id ? `Customer #${id}` : "");
  if (!label && !id) return null;
  return {
    id: id || null,
    label,
    businessName: customer?.businessName || label,
    emailAddress: customer?.emailAddress || "",
  };
};
const normalizeSavedRepOption = (rep) => {
  if (!rep) return null;
  const id = rep?.id != null && String(rep.id).trim() !== "" ? String(rep.id) : "";
  const normalized = {
    id: id || null,
    first_name: rep?.first_name || rep?.firstName || "",
    last_name: rep?.last_name || rep?.lastName || "",
    firstName: rep?.firstName || rep?.first_name || "",
    lastName: rep?.lastName || rep?.last_name || "",
    email: rep?.email || "",
    profilePicture: rep?.profilePicture || null,
    avatar: rep?.avatar || null,
    photoUrl: rep?.photoUrl || null,
    role: rep?.role || "",
    roleLabel: rep?.roleLabel || "",
    label: rep?.label || "",
  };
  const fallbackLabel = repDisplayName(normalized);
  if (!normalized.id && !fallbackLabel) return null;
  normalized.label = normalized.label || fallbackLabel;
  return normalized;
};

const ReportCustomerAsyncSelect = ({
  instanceUniqueId,
  value,
  onChange,
  seedOptions = [],
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState(Array.isArray(seedOptions) ? seedOptions : []);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    setInputValue(value ? customerOptionLabel(value) : "");
  }, [value]);

  useEffect(() => {
    setOptions((previous) => {
      const map = new Map(previous.map((option) => [String(option?.id || ""), option]));
      (Array.isArray(seedOptions) ? seedOptions : []).forEach((option) => {
        if (!option) return;
        map.set(String(option?.id || ""), option);
      });
      if (value?.id) map.set(String(value.id), value);
      return Array.from(map.values()).filter((option) => option && option.id != null);
    });
  }, [seedOptions, value]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      const requestId = ++requestSeq.current;
      setLoading(true);
      try {
        const res = await searchReportCustomers({
          instanceUniqueId,
          q: inputValue,
          page: 1,
          limit: 25,
        });
        if (requestId !== requestSeq.current) return;
        const customers = Array.isArray(res?.customers) ? res.customers : [];
        setOptions((previous) => {
          const map = new Map();
          customers.forEach((customer) => {
            if (!customer?.id) return;
            map.set(String(customer.id), {
              id: customer.id,
              businessName: customer.businessName || customer.name || customer.customerName || customer.emailAddress || `Customer #${customer.id}`,
              emailAddress: customer.emailAddress || "",
              label: customer.businessName || customer.name || customer.customerName || customer.emailAddress || `Customer #${customer.id}`,
            });
          });
          (Array.isArray(seedOptions) ? seedOptions : []).forEach((option) => {
            if (!option?.id || map.has(String(option.id))) return;
            map.set(String(option.id), option);
          });
          if (value?.id && !map.has(String(value.id))) map.set(String(value.id), value);
          return Array.from(map.values());
        });
      } catch (error) {
        if (requestId !== requestSeq.current) return;
        console.error("Failed to search report customers", error);
      } finally {
        if (requestId === requestSeq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [open, inputValue, instanceUniqueId, seedOptions, value]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      disabled={disabled}
      loading={loading}
      filterOptions={(x) => x}
      inputValue={inputValue}
      onInputChange={(_, nextValue, reason) => {
        if (reason === "reset") {
          setInputValue(value ? customerOptionLabel(value) : "");
          return;
        }
        if (reason === "clear") {
          setInputValue("");
          return;
        }
        setInputValue(nextValue);
      }}
      onChange={(_, option) => {
        setInputValue(option ? customerOptionLabel(option) : "");
        onChange(option || null);
      }}
      isOptionEqualToValue={(option, currentValue) => String(option?.id || "") === String(currentValue?.id || "")}
      getOptionLabel={customerOptionLabel}
      renderOption={(props, option) => (
        <li {...props} key={option?.id || option?.label}>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography variant="body2" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {customerOptionLabel(option)}
            </Typography>
            {option?.emailAddress ? (
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                {option.emailAddress}
              </Typography>
            ) : null}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Customer"
          placeholder="Search customers"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const RepCell = ({ rep, fallback = "—" }) => {
  const name = repDisplayName(rep);
  if (!rep || !name) return fallback;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Avatar
        src={normalizeAvatarUrl(rep?.profilePicture || rep?.avatar || rep?.photoUrl) || undefined}
        alt={name}
        sx={{ width: 24, height: 24, fontSize: 10.5 }}
      >
        {repInitials(name)}
      </Avatar>
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Box>
    </Box>
  );
};

const getSortedRows = (rows, columns, sortBy, sortDir) => {
  const safeRows = Array.isArray(rows) ? [...rows] : [];
  if (!sortBy) return safeRows;
  const column = columns.find((item) => (item.field || item.headerName) === sortBy);
  if (!column) return safeRows;
  const valueFor = (row) => {
    const raw = typeof column.sortValue === "function" ? column.sortValue({ row, value: row?.[column.field] }) : row?.[column.field];
    if (column.sortType === "number" || column.sortType === "currency") return numberValue(raw);
    if (column.sortType === "date") {
      const date = raw ? new Date(raw) : null;
      return Number.isNaN(date?.getTime?.()) ? 0 : date.getTime();
    }
    return String(raw || "").toLowerCase();
  };
  safeRows.sort((left, right) => {
    if (left?.rowType === "customerSummary" && right?.rowType !== "customerSummary" && sortBy === "customerName") return -1;
    if (left?.rowType !== "customerSummary" && right?.rowType === "customerSummary" && sortBy === "customerName") return 1;
    const a = valueFor(left);
    const b = valueFor(right);
    let delta = 0;
    if (typeof a === "number" && typeof b === "number") delta = a - b;
    else delta = String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
    return sortDir === "desc" ? -delta : delta;
  });
  return safeRows;
};

const ReportTable = ({
  rows = [],
  columns = [],
  sortBy = "",
  sortDir = "asc",
  onSort = () => {},
  emptyMessage = "Nothing to show.",
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];

  if (!safeRows.length) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography>{emptyMessage}</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...reportTableContainerSx,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "auto",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 1580 }}>
        <TableHead>
          <TableRow>
            {safeColumns.map((column) => {
              const columnKey = column.field || column.headerName;
              const active = sortBy === columnKey;
              const sortable = column.sortable !== false;
              return (
                <TableCell
                  key={columnKey}
                  sx={reportTableHeaderCellSx({ minWidth: column.minWidth || 120 })}
                  align={column.align || "left"}
                >
                  <Box
                    onClick={() => sortable && onSort(columnKey)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: sortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    <span>{column.headerName}</span>
                    {sortable ? (
                      active ? (sortDir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />) : <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                    ) : null}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={reportTableBodySx}>
          {safeRows.map((row, index) => {
            const isSummaryRow = row?.rowType === "customerSummary";
            return (
              <TableRow
                key={row?.id || index}
                hover={!isSummaryRow}
                sx={isSummaryRow ? {
                  backgroundColor: "rgba(59, 130, 246, 0.06)",
                  "& td": {
                    fontWeight: 700,
                    borderTop: "1px solid rgba(59, 130, 246, 0.18)",
                  },
                } : undefined}
              >
                {safeColumns.map((column) => {
                  const value = row?.[column.field];
                  const rendered = typeof column.renderCell === "function"
                    ? column.renderCell({ value, row })
                    : (value == null || value === "" ? "—" : value);
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align || "left"}
                      sx={{
                        whiteSpace: column.wrap ? "normal" : "nowrap",
                        verticalAlign: "top",
                        ...(column.money ? { fontVariantNumeric: "tabular-nums" } : {}),
                      }}
                    >
                      {rendered}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ArAgingReport = () => {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const selfScopedViewer = ["client-rep", "order-rep"].includes(roleNormalized(authState?.role));
  const forcedAssignmentFilter = roleNormalized(authState?.role) === "order-rep" ? "orderRep" : "clientRep";

  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : DEFAULT_FILTERS.assignmentFilter,
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [meta, setMeta] = useState({ generatedAt: "", asOfDate: DEFAULT_FILTERS.asOfDate, totalRows: 0, appliedFilters: {} });
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [repOptions, setRepOptions] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(["rowTypeLabel"]);
  const [sortBy, setSortBy] = useState("customerName");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);
  const skipNextAutoLoadRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("eliteCore:reports:arAging:columns");
      const parsed = raw ? JSON.parse(raw) : ["rowTypeLabel"];
      setHiddenColumns(Array.isArray(parsed) && parsed.length ? parsed.map(String) : ["rowTypeLabel"]);
    } catch {
      setHiddenColumns(["rowTypeLabel"]);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("eliteCore:reports:arAging:columns", JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  useEffect(() => {
    if (!selfScopedViewer) return;
    setFilters((previous) => ({
      ...previous,
      assignmentFilter: forcedAssignmentFilter,
      repId: "",
    }));
    setSelectedRep(null);
  }, [forcedAssignmentFilter, selfScopedViewer]);

  const load = useCallback(async (filtersOverride = null) => {
    const effectiveFilters = filtersOverride && typeof filtersOverride === "object"
      ? filtersOverride
      : filters;
    const params = {
      instanceUniqueId,
      ...effectiveFilters,
      includeCredits: String(effectiveFilters.includeCredits) === "true",
    };
    const requestId = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await fetchArAgingReport(params);
      if (requestId !== requestSeq.current) return;
      setRows(Array.isArray(res?.rows) ? res.rows : []);
      setSummary(res?.summary && typeof res.summary === "object" ? { ...DEFAULT_SUMMARY, ...res.summary } : DEFAULT_SUMMARY);
      setMeta(res?.meta && typeof res.meta === "object" ? res.meta : { generatedAt: "", asOfDate: effectiveFilters.asOfDate, totalRows: 0, appliedFilters: {} });
      setOptions(res?.options && typeof res.options === "object" ? {
        buckets: Array.isArray(res.options.buckets) ? res.options.buckets : DEFAULT_OPTIONS.buckets,
        assignmentFilters: Array.isArray(res.options.assignmentFilters) ? res.options.assignmentFilters : DEFAULT_OPTIONS.assignmentFilters,
      } : DEFAULT_OPTIONS);
      const nextCustomerOptions = Array.isArray(res?.customerOptions) ? res.customerOptions : [];
      setCustomerOptions(nextCustomerOptions);
      const nextRepOptions = Array.isArray(res?.repOptions) ? res.repOptions : [];
      setRepOptions(nextRepOptions);
      if (!effectiveFilters.clientId) {
        setSelectedCustomer(null);
      } else {
        const matched = nextCustomerOptions.find((option) => String(option?.id || "") === String(effectiveFilters.clientId));
        setSelectedCustomer((previous) => matched || previous);
      }
      if (!effectiveFilters.repId) {
        setSelectedRep(null);
      } else {
        const matchedRep = nextRepOptions.find((option) => String(option?.id || "") === String(effectiveFilters.repId));
        setSelectedRep((previous) => matchedRep || previous);
      }
    } catch (error) {
      if (requestId !== requestSeq.current) return;
      console.error("Failed to load A/R aging report", error);
      setRows([]);
      setSummary(DEFAULT_SUMMARY);
      setMeta({ generatedAt: "", asOfDate: effectiveFilters.asOfDate, totalRows: 0, appliedFilters: {} });
      setCustomerOptions([]);
      setRepOptions([]);
      setOptions(DEFAULT_OPTIONS);
    } finally {
      if (requestId === requestSeq.current) setLoading(false);
    }
  }, [filters, instanceUniqueId]);

  useEffect(() => {
    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }
    load();
  }, [load]);

  const columns = useMemo(() => ([
    { field: "rowTypeLabel", headerName: "Row Type", minWidth: 140, sortType: "string" },
    {
      field: "customerName",
      headerName: "Customer",
      minWidth: 220,
      sortType: "string",
      renderCell: ({ value, row }) => row?.rowType === "invoiceDetail"
        ? <Box sx={{ pl: 2 }}>{value || "—"}</Box>
        : (value || "—"),
    },
    { field: "invoiceNumber", headerName: "Invoice #", minWidth: 120, sortType: "string" },
    { field: "orderNumber", headerName: "Order #", minWidth: 120, sortType: "string" },
    { field: "projectTitle", headerName: "Project", minWidth: 220, sortType: "string", wrap: true },
    {
      field: "clientRep",
      headerName: "Client Rep",
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => row?.rowType === "customerSummary" ? "—" : <RepCell rep={row.clientRepDetail} fallback={row.clientRep || "—"} />,
    },
    {
      field: "orderRep",
      headerName: "Order Rep",
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => row?.rowType === "customerSummary" ? "—" : <RepCell rep={row.orderRepDetail} fallback={row.orderRep || "—"} />,
    },
    { field: "invoiceDate", headerName: "Invoice Date", minWidth: 130, sortType: "date", renderCell: ({ value }) => friendlyDate(value) },
    { field: "dueDate", headerName: "Due Date", minWidth: 130, sortType: "date", renderCell: ({ value }) => friendlyDate(value) },
    { field: "daysPastDue", headerName: "Days Past Due", minWidth: 120, sortType: "number", align: "right" },
    { field: "currentAmount", headerName: "Current", minWidth: 130, sortType: "currency", align: "right", money: true, renderCell: ({ value }) => currency(value) },
    { field: "oneToThirtyAmount", headerName: "1 - 30", minWidth: 130, sortType: "currency", align: "right", money: true, renderCell: ({ value }) => currency(value) },
    { field: "thirtyOneToSixtyAmount", headerName: "31 - 60", minWidth: 130, sortType: "currency", align: "right", money: true, renderCell: ({ value }) => currency(value) },
    { field: "sixtyOneToNinetyAmount", headerName: "61 - 90", minWidth: 130, sortType: "currency", align: "right", money: true, renderCell: ({ value }) => currency(value) },
    { field: "ninetyOnePlusAmount", headerName: "91 and Over", minWidth: 145, sortType: "currency", align: "right", money: true, renderCell: ({ value }) => currency(value) },
    { field: "openBalance", headerName: "Total", minWidth: 130, sortType: "currency", align: "right", money: true, renderCell: ({ value }) => currency(value) },
  ]), []);

  useEffect(() => {
    const validColumnKeys = new Set(columns.map((column) => String(column.field || column.headerName || "")));
    setHiddenColumns((previous) => {
      const next = previous.filter((key) => validColumnKeys.has(String(key)));
      return next.length >= validColumnKeys.size ? ["rowTypeLabel"] : next;
    });
  }, [columns]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenColumns.includes(String(column.field || column.headerName || ""))),
    [columns, hiddenColumns],
  );

  const sortedRows = useMemo(() => getSortedRows(rows, columns, sortBy, sortDir), [columns, rows, sortBy, sortDir]);

  const onSort = useCallback((columnKey) => {
    setSortBy((previous) => {
      if (previous === columnKey) {
        setSortDir((current) => current === "asc" ? "desc" : "asc");
        return previous;
      }
      setSortDir("asc");
      return columnKey;
    });
  }, []);

  const currentSavedReportState = useMemo(() => ({
    version: 1,
    filters: {
      asOfDate: filters.asOfDate || "",
      client: filters.client || "",
      clientId: filters.clientId || "",
      repId: filters.repId || "",
      assignmentFilter: filters.assignmentFilter || "anyAssignment",
      bucket: filters.bucket || "all",
      quickFind: filters.quickFind || "",
      includeCredits: String(filters.includeCredits) === "true",
    },
    selectedCustomer: normalizeSavedCustomerOption(selectedCustomer),
    selectedRep: normalizeSavedRepOption(selectedRep),
    hiddenColumns: Array.isArray(hiddenColumns) ? hiddenColumns.filter(Boolean) : [],
    sortBy,
    sortDir,
  }), [filters, hiddenColumns, selectedCustomer, selectedRep, sortBy, sortDir]);

  const applySavedReportState = useCallback(async (savedState = {}) => {
    const savedFilters = savedState?.filters && typeof savedState.filters === "object" ? savedState.filters : {};
    const savedCustomer = normalizeSavedCustomerOption(savedState?.selectedCustomer)
      || normalizeSavedCustomerOption(savedFilters?.clientId ? { id: savedFilters.clientId, label: savedFilters.client || `Customer #${savedFilters.clientId}` } : null);
    const savedRep = normalizeSavedRepOption(savedState?.selectedRep)
      || normalizeSavedRepOption(savedFilters?.repId ? { id: savedFilters.repId, label: `User ${savedFilters.repId}` } : null);
    const validColumnKeys = new Set(columns.map((column) => String(column.field || column.headerName || "")));

    let nextHiddenColumns = normalizeSavedHiddenColumns({
      savedState,
      validColumnKeys: Array.from(validColumnKeys),
      fallbackHiddenColumns: ["rowTypeLabel"],
    });
    if (!nextHiddenColumns.length) nextHiddenColumns = ["rowTypeLabel"];
    if (nextHiddenColumns.length >= validColumnKeys.size) nextHiddenColumns = ["rowTypeLabel"];

    setSelectedCustomer(savedCustomer);
    setSelectedRep(savedRep);
    if (savedCustomer?.id) {
      setCustomerOptions((previous) => previous.some((option) => String(option?.id || "") === String(savedCustomer.id)) ? previous : [...previous, savedCustomer]);
    }
    if (savedRep?.id) {
      setRepOptions((previous) => previous.some((option) => String(option?.id || "") === String(savedRep.id)) ? previous : [...previous, savedRep]);
    }
    const nextFilters = {
      ...DEFAULT_FILTERS,
      ...savedFilters,
      asOfDate: String(savedFilters?.asOfDate || DEFAULT_FILTERS.asOfDate),
      client: savedCustomer ? customerOptionLabel(savedCustomer) : String(savedFilters?.client || ""),
      clientId: savedCustomer?.id ? String(savedCustomer.id) : "",
      repId: selfScopedViewer ? "" : (savedRep?.id ? String(savedRep.id) : ""),
      assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : String(savedFilters?.assignmentFilter || "anyAssignment"),
      bucket: String(savedFilters?.bucket || "all"),
      quickFind: String(savedFilters?.quickFind || ""),
      includeCredits: parseSavedBoolean(savedFilters?.includeCredits, false) ? "true" : "false",
    };

    setHiddenColumns(nextHiddenColumns);
    setSortBy(String(savedState?.sortBy || "customerName"));
    setSortDir(savedState?.sortDir === "desc" ? "desc" : "asc");
    skipNextAutoLoadRef.current = true;
    setFilters(nextFilters);
    await load(nextFilters);
  }, [columns, forcedAssignmentFilter, load, selfScopedViewer]);

  const onExport = async () => {
    const response = await exportArAgingCsv({
      instanceUniqueId,
      ...filters,
      includeCredits: String(filters.includeCredits) === "true",
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "ar-aging-report.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const summaryCards = useMemo(() => ([
    {
      key: "total-ar",
      label: "Total A/R",
      value: currency(summary.totalAr),
      tone: numberValue(summary.totalAr) < 0 ? "danger" : "sales",
      helperText: `Outstanding receivables open as of ${friendlyDate(meta?.asOfDate)}.`,
    },
    { key: "current", label: "Current", value: currency(summary.current), tone: "count" },
    { key: "1-30", label: "1 - 30", value: currency(summary.oneToThirty), tone: "warning" },
    { key: "31-60", label: "31 - 60", value: currency(summary.thirtyOneToSixty), tone: "warning" },
    { key: "61-90", label: "61 - 90", value: currency(summary.sixtyOneToNinety), tone: "danger" },
    { key: "91+", label: "91 and Over", value: currency(summary.ninetyOnePlus), tone: "danger" },
    { key: "past-due", label: "Past Due Total", value: currency(summary.pastDueTotal), tone: "budget" },
    { key: "customers", label: "Customers with Balance", value: summary.customerCount ?? 0, tone: "count" },
  ]), [meta?.asOfDate, summary]);

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box className="fade-in-effect-usability" sx={reportPageWrapperSx}>
          <UITableShell customLayout paperSx={reportShellSx}>
            <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                    A/R Aging Report
                  </Typography>
                  <Chip
                    label={loading ? "Loading..." : `${meta?.totalRows ?? 0} invoices`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                  <Chip
                    label="arAging"
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-info-bg)",
                      color: "var(--ec-status-info-text)",
                      border: "1px solid var(--ec-status-info-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                  <Chip
                    label={`As of ${friendlyDate(meta?.asOfDate || filters.asOfDate)}`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-accent-bg, rgba(99,102,241,0.12))",
                      color: "var(--ec-status-accent-text, #4338ca)",
                      border: "1px solid var(--ec-status-accent-border, rgba(99,102,241,0.32))",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ReportSavedReportsButton
                    instanceUniqueId={instanceUniqueId}
                    reportKey="arAging"
                    currentState={currentSavedReportState}
                    onApplyReport={applySavedReportState}
                  />
                  <ReportColumnVisibilityButton
                    columns={columns}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                  />
                  <Tooltip title="Row density">
                    <span>
                      <IconButton size="small" disabled sx={{ color: "text.secondary" }}>
                        <SettingsIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={onExport}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 34,
                      px: 2,
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
            </UITableToolbar>

            <Box sx={{ ...reportContentSx, minWidth: 0, maxWidth: "100%" }}>
              <Paper elevation={0} sx={reportFilterCardSx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  A/R Aging Report Controls
                </Typography>
                <Box sx={reportFilterGridSx}>
                  <TextField
                    size="small"
                    type="date"
                    label="As Of Date"
                    value={filters.asOfDate}
                    onChange={(event) => setFilters((previous) => ({ ...previous, asOfDate: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <ReportCustomerAsyncSelect
                    instanceUniqueId={instanceUniqueId}
                    value={selectedCustomer}
                    seedOptions={customerOptions}
                    onChange={(customer) => {
                      setSelectedCustomer(customer);
                      setFilters((previous) => ({
                        ...previous,
                        clientId: customer?.id ? String(customer.id) : "",
                        client: customer ? customerOptionLabel(customer) : "",
                      }));
                    }}
                  />
                  {!selfScopedViewer ? (
                    <ReportRepAsyncSelect
                      instanceUniqueId={instanceUniqueId}
                      value={selectedRep}
                      searchFn={fetchArAgingReportReps}
                      roles={["client-rep", "order-rep", "administrator", "admin"]}
                      seedOptions={repOptions}
                      label="Rep"
                      onChange={(rep) => {
                        setSelectedRep(rep);
                        setFilters((previous) => ({
                          ...previous,
                          repId: rep?.id ? String(rep.id) : "",
                        }));
                      }}
                    />
                  ) : null}
                  {!selfScopedViewer ? (
                    <TextField
                      select
                      size="small"
                      label="Assignment Filter"
                      value={filters.assignmentFilter}
                      onChange={(event) => {
                        setSelectedRep(null);
                        setFilters((previous) => ({ ...previous, assignmentFilter: event.target.value, repId: "" }));
                      }}
                      helperText="Controls which assignment source determines row inclusion."
                    >
                      {(options.assignmentFilters || DEFAULT_OPTIONS.assignmentFilters).map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  ) : null}
                  <TextField
                    select
                    size="small"
                    label="Bucket"
                    value={filters.bucket}
                    onChange={(event) => setFilters((previous) => ({ ...previous, bucket: event.target.value }))}
                  >
                    {(options.buckets || DEFAULT_OPTIONS.buckets).map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="Quick Find"
                    value={filters.quickFind}
                    onChange={(event) => setFilters((previous) => ({ ...previous, quickFind: event.target.value }))}
                  />
                  <TextField
                    select
                    size="small"
                    label="Include Credits / Negative Balances"
                    value={filters.includeCredits}
                    onChange={(event) => setFilters((previous) => ({ ...previous, includeCredits: event.target.value }))}
                  >
                    <MenuItem value="false">Exclude Credits</MenuItem>
                    <MenuItem value="true">Include Credits</MenuItem>
                  </TextField>
                </Box>
              </Paper>

              <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", xl: "repeat(4,minmax(0,1fr))" } }}>
                {summaryCards.map((card) => (
                  <ReportSummaryCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    tone={card.tone}
                    helperText={card.helperText}
                  />
                ))}
              </Box>

              <Box sx={{ ...reportTableSectionSx, minWidth: 0, maxWidth: "100%" }}>
                <Paper elevation={0} sx={{ ...reportTablePaperSx, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                  <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto", overflowY: "hidden" }}>
                      {loading ? (
                        <Box sx={{ p: 4 }}>
                          <Typography>Loading A/R aging report...</Typography>
                        </Box>
                      ) : (
                        <ReportTable
                          rows={sortedRows}
                          columns={visibleColumns}
                          sortBy={sortBy}
                          sortDir={sortDir}
                          onSort={onSort}
                          emptyMessage="No open receivables found for the selected filters."
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </UITableShell>
        </Box>
      </div>
    </div>
  );
};

export default ArAgingReport;
